/**
 * Magnet — a little horseshoe docked at the hero's corner. Drag it over the
 * picture and the tube bends: curvature, colour fringing and line tearing
 * follow the magnet (glitchGL's cursor interaction does the local part; we
 * crank the global warp while it's held). Let go and it snaps home and the
 * picture relaxes.
 */
export class Magnet {
  constructor(host, { background }) {
    this.bg = background;
    this.el = document.createElement('button');
    this.el.type = 'button';
    this.el.className = 'magnet';
    this.el.setAttribute('aria-label', 'Magnet — drag over the picture to bend it');
    this.el.title = 'Drag me';
    this.el.innerHTML = `<span class="magnet__body" aria-hidden="true">
      <svg viewBox="0 0 40 40" width="40" height="40" fill="none" stroke-linejoin="round">
        <path d="M8 4v14a12 12 0 0 0 24 0V4h-9v14a3 3 0 0 1-6 0V4z" fill="#e2362f" stroke="#7a1410" stroke-width="1.6" />
        <path d="M8 4h9v7H8zM23 4h9v7h-9z" fill="#d9d9d9" stroke="#6b6b6b" stroke-width="1.4" />
        <path class="magnet__spark" d="M3 28l3 2M37 28l-3 2M5 23l2 1M35 23l-2 1" stroke="#fff" stroke-width="1.6" stroke-linecap="round" />
      </svg></span>
      <span class="magnet__label"><b>magnet</b><i>drag me</i></span>`;
    host.appendChild(this.el);
    // The field: a fixed full-screen layer whose backdrop-filter (hue swing,
    // oversaturation, smear) is masked to a radial blob under the magnet.
    this.field = document.createElement('div');
    this.field.className = 'magnet-field';
    this.field.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.field);
    this._hue = 0;
    this.dragging = false;
    this.el.addEventListener('pointerdown', this._down);
  }

  _down = (e) => {
    e.preventDefault();
    this.dragging = true;
    this.el.setPointerCapture(e.pointerId);
    this.el.classList.add('is-dragging');
    document.body.classList.add('is-magnet');
    this.field.classList.add('is-on');
    this._move(e);
    this.el.addEventListener('pointermove', this._move);
    this.el.addEventListener('pointerup', this._up, { once: true });
    this.el.addEventListener('pointercancel', this._up, { once: true });
  };

  _move = (e) => {
    if (!this.dragging) return;
    this.el.style.setProperty('--mx', `${e.clientX}px`);
    this.el.style.setProperty('--my', `${e.clientY}px`);
    // Field follows the magnet; the hue swings with movement speed like a
    // tube's shadow mask losing alignment.
    const speed = Math.hypot(e.movementX || 0, e.movementY || 0);
    this._hue = (this._hue + speed * 1.6) % 360;
    this.field.style.setProperty('--mx', `${e.clientX}px`);
    this.field.style.setProperty('--my', `${e.clientY}px`);
    this.field.style.setProperty('--hue', `${this._hue.toFixed(0)}deg`);
    this.field.style.setProperty('--r', `${Math.round(160 + Math.min(160, speed * 6))}px`);
  };

  _up = () => {
    this.dragging = false;
    this.el.removeEventListener('pointermove', this._move);
    this.el.classList.remove('is-dragging');
    document.body.classList.remove('is-magnet');
    this.el.style.removeProperty('--mx');
    this.el.style.removeProperty('--my');
    this.field.classList.remove('is-on');
    // degauss thump
    this.bg.triggerGlitch({ intensity: 2.2 });
    document.body.classList.add('is-degauss');
    setTimeout(() => document.body.classList.remove('is-degauss'), 420);
  };
}
